import type { Deck, User, Slide, Node } from '../../../types';
import { apiNodeToFrontend } from '../../../utils/nodeTransformers';
import { buildApiUrl } from '../../../utils/apiHelpers';
import { logger } from '../../../utils/logger';

interface UseDeckDataProps {
  deckId: string;
  userSlugOrId: string;
  slideIdParam?: string;
  setDeck: (deck: Deck | null | ((prev: Deck | null) => Deck | null)) => void;
  setUser: (user: User | null) => void;
  setCurrentUser: (user: User | null) => void;
  setSlides: (slides: Slide[]) => void;
  setCurrentSlideIndex: (index: number) => void;
  setCurrentSlideId: (id: number | null) => void;
  setNodes: (nodes: Node[]) => void;
  setSlideNodesCache: (cache: { [slideId: number]: Node[] } | ((prev: { [slideId: number]: Node[] }) => { [slideId: number]: Node[] })) => void;
  setLoading: (loading: boolean) => void;
}

/**
 * Hook for managing deck and slide data loading
 *
 * Handles:
 * - Fetching deck data from API
 * - Loading slide nodes
 * - Creating first slide if none exist
 * - Transforming API responses to frontend format
 */
export function useDeckData({
  deckId,
  userSlugOrId,
  slideIdParam,
  setDeck,
  setUser,
  setCurrentUser,
  setSlides,
  setCurrentSlideIndex,
  setCurrentSlideId,
  setNodes,
  setSlideNodesCache,
  setLoading,
}: UseDeckDataProps) {
  const apiUrl = (path: string) => buildApiUrl(userSlugOrId, deckId, path);

  const loadSlideNodes = async (slide: Slide): Promise<Node[]> => {
    setCurrentSlideId(slide.id);

    try {
      const response = await fetch(apiUrl(`/slides/${slide.id}/nodes`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load nodes');

      const data = await response.json();
      const freshNodes = data.nodes || [];

      if (freshNodes.length > 0) {
        logger.debug('render', 'Loading slide nodes', {
          nodeCount: freshNodes.length,
          nodes: freshNodes
        });

        const convertedNodes = freshNodes.map(apiNodeToFrontend);
        setNodes(convertedNodes);
        setSlideNodesCache(prev => ({ ...prev, [slide.id]: convertedNodes }));
        return convertedNodes;
      } else {
        setNodes([]);
        setSlideNodesCache(prev => ({ ...prev, [slide.id]: [] }));
        return [];
      }
    } catch (error) {
      logger.error('api', 'Failed to load nodes', error);
      setNodes([]);
      setSlideNodesCache(prev => ({ ...prev, [slide.id]: [] }));
      return [];
    }
  };

  const createFirstSlide = async () => {
    try {
      const response = await fetch(apiUrl(`/${deckId}/slides`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slide: {
            position: 0,
            locked: false,
            z_index: 1
          }
        }),
      });

      if (!response.ok) throw new Error('Failed to create slide');

      const data = await response.json();
      const newSlide = data.slide;
      setSlides([newSlide]);
      setCurrentSlideIndex(0);
      setCurrentSlideId(newSlide.id);
      setNodes([]);

      setDeck(prev => prev ? {
        ...prev,
        slides: [newSlide]
      } : null);
    } catch (error) {
      console.error('Failed to create first slide:', error);
    }
  };

  const loadDeckData = async () => {
    try {
      setLoading(true);

      const deckResponse = await fetch(apiUrl(`/${deckId}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!deckResponse.ok) throw new Error('Failed to load deck');

      const deckData = await deckResponse.json();

      const token = localStorage.getItem('token');
      if (token) {
        try {
          const userResponse = await fetch('/api/v1/auth/me', {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (userResponse.ok) {
            const userData = await userResponse.json();
            setCurrentUser(userData.user);
          }
        } catch (error) {
          logger.debug('api', 'Not authenticated', error);
        }
      }

      if (deckData.deck.user_id) {
        setUser({ id: deckData.deck.user_id, slug: userSlugOrId });
      }

      const response = await fetch(apiUrl(`/${deckId}/slides`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load deck');

      const data = await response.json();
      setDeck({ ...deckData.deck, slides: data.slides });

      if (!data.slides || data.slides.length === 0) {
        await createFirstSlide();
      } else {
        setSlides(data.slides);

        let targetSlide = data.slides[0];
        let targetIndex = 0;

        if (slideIdParam) {
          const slideIndex = data.slides.findIndex((s: Slide) => s.public_id === slideIdParam || s.id === parseInt(slideIdParam));
          if (slideIndex !== -1) {
            targetSlide = data.slides[slideIndex];
            targetIndex = slideIndex;
          }
        }

        setCurrentSlideIndex(targetIndex);
        await loadSlideNodes(targetSlide);
      }
    } catch (error) {
      console.error('Failed to load deck:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    loadDeckData,
    loadSlideNodes,
    createFirstSlide,
  };
}
